<template>
  <div id="app">
    <!-- Show loading state -->
    <div v-if="initializing" class="loading-screen">
      <img src="@/assets/action-logo.svg" alt="Action Network Logo" class="loading-logo">
    </div>

    <!-- Only show app content after initialization -->
    <template v-else>
      <!-- Show Terms & Conditions if not accepted -->
      <TermsAndConditions v-if="showTerms" />
      
      <!-- Show main app content if terms are accepted -->
      <template v-else>
        <!-- Desktop Header -->
        <header v-if="user && $route.path !== '/login' && !isMobile" class="dashboard-header">
          <div class="logo-container">
            <img src="@/assets/action-logo.svg" alt="Action Network Logo" class="logo">
          </div>
          <div class="header-right">
            <div class="user-mini-profile" v-if="user">
              <img 
                :src="profilePhotoUrl || DEFAULT_AVATAR" 
                alt="Profile" 
                class="header-profile-image"
                @error="handleImageError"
                referrerpolicy="no-referrer"
              />
              <span>{{ user?.displayName }}</span>
            </div>
            <nav class="nav-menu" v-if="user">
              <router-link to="/profile" class="nav-btn">Profile</router-link>
            </nav>
            <button v-if="user" @click="logout" class="logout-btn">Logout</button>
          </div>
        </header>

        <!-- Mobile Header -->
        <header v-if="user && $route.path !== '/login' && isMobile" class="mobile-header">
        </header>
        
        <!-- Main Navigation -->
        <MainNavigation v-if="user && $route.path !== '/login'" :isAdmin="isAdmin" />

        <router-view 
          @photo-updated="updateProfilePhoto" 
          :current-user-id="currentUser?.uid?.toString()"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import MainNavigation from './components/MainNavigation.vue';
import { userService } from './services/api';
import defaultAvatar from '@/assets/default-avatar.png';
import TermsAndConditions from './components/TermsAndConditions.vue';

export default {
  name: 'App',
  components: {
    MainNavigation,
    TermsAndConditions
  },
  setup() {
    const user = ref(null);
    const isAdmin = ref(false);
    const router = useRouter();
    const route = useRoute();
    const profilePhotoUrl = ref(null);
    const DEFAULT_AVATAR = defaultAvatar;
    const initializing = ref(true);
    const isMobile = ref(window.innerWidth <= 768);
    const currentUser = ref(null);
    const showTerms = ref(false);

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    // Extract auth initialization logic
    const initializeNativeAuth = async () => {
      const token = localStorage.getItem('auth_token');
      
      if (token) {
        try {
          const profile = await userService.getProfile();
          
          user.value = {
            uid: profile.id,
            email: profile.email,
            displayName: profile.display_name || `${profile.first_name} ${profile.last_name}`.trim()
          };
          currentUser.value = user.value;
          profilePhotoUrl.value = profile.photo_url;
          isAdmin.value = profile.role === 'admin';
          // TODO: Re-enable terms check once route is fixed
          showTerms.value = false;
        } catch (error) {
          console.error('[App.vue] Error loading profile:', error);
          localStorage.removeItem('auth_token');
          user.value = null;
          currentUser.value = null;
          isAdmin.value = false;
          showTerms.value = false;
        }
      } else {
        user.value = null;
        currentUser.value = null;
        isAdmin.value = false;
        showTerms.value = false;
      }
      initializing.value = false;
    };

    // Watch route changes for native auth
    watch(
      () => route.path,
      (newPath) => {
        // Re-check auth when navigating to protected routes
        if (newPath !== '/login' && newPath !== '/reset-password') {
          initializeNativeAuth();
        }
      }
    );

    onMounted(() => {
      // Initialize native auth
      initializeNativeAuth();
      window.addEventListener('resize', checkMobile);
    });

    onMounted(async () => {
      // Test API connection
      // console.log('\n=== Testing API Connection ===');
      // const result = await testAPI();
      // console.log('API Test Result:', result);
    });

    const handleImageError = (e) => {
      e.target.src = DEFAULT_AVATAR;
    };

    const updateProfilePhoto = (photoUrl) => {
      profilePhotoUrl.value = photoUrl;
    };

    const logout = async () => {
      try {
        // For native auth, just clear the token
        localStorage.removeItem('auth_token');
        user.value = null;
        currentUser.value = null;
        isAdmin.value = false;
        showTerms.value = false;
        
        router.push('/login');
      } catch (error) {
        console.error('Error logging out:', error);
      }
    };

    return {
      user,
      isAdmin,
      profilePhotoUrl,
      handleImageError,
      logout,
      DEFAULT_AVATAR,
      updateProfilePhoto,
      initializing,
      isMobile,
      currentUser,
      showTerms
    };
  }
};
</script>

<style>
body {
  background-color: #ebf8ff;
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
      Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

/* Copy all the relevant styles from UserDashboard.vue */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.logo-container {
  padding: 20px 0;
}

.logo {
  height: 50px;
  width: auto;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.nav-menu {
  display: flex;
  gap: 10px;
  margin-right: 20px;
}

.nav-btn {
  text-decoration: none;
  display: inline-block;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #486581;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-btn:hover {
  background: #f0f4f8;
}

.nav-btn.router-link-active {
  background: #4285f4;
  color: white;
}

.user-mini-profile {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-profile-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.logout-btn {
  background-color: #dc3545;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.logout-btn:hover {
  background-color: #c82333;
}

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
}

.loading-logo {
  width: 200px;
  height: auto;
  opacity: 0.8;
}

.mobile-header {
  display: none;
}

@media (max-width: 768px) {
  .dashboard-header {
    display: none;
  }

  .mobile-nav {
    position: sticky;
    top: 0;
    z-index: 100;
  }
}
</style>
